import type { EditingPlanType } from "@app/components/poke/plans/form";
import {
  Field,
  fromPlanType,
  PLAN_FIELDS,
  toPlanType,
  useEditingPlan,
} from "@app/components/poke/plans/form";
import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import { usePokePlans } from "@app/lib/swr/poke";
import { usePokePageMetadata } from "@app/poke/swr/currentPage";
import type { PlanTypeSchema } from "@app/types/api/poke/plans";
import type { PlanType } from "@app/types/plan";
import {
  Button,
  Check,
  Edit04,
  IconButton,
  Plus,
  Spinner,
  XClose,
} from "@dust-tt/sparkle";
import React from "react";
import { useSWRConfig } from "swr";
import type { z } from "zod";

export function PlansPage() {
  usePokePageMetadata({ name: "Plans" });

  const { mutate } = useSWRConfig();

  const sendNotification = useSendNotification();

  const { plans, isPlansLoading } = usePokePlans();

  const { editingPlan, resetEditingPlan, createNewPlan, setEditingPlan } =
    useEditingPlan();

  const handleSavePlan = async () => {
    if (!editingPlan) {
      sendNotification({
        title: "Error saving plan",
        type: "error",
        description: "Something went wrong (editingPlan is null)",
      });
      return;
    }
    const errors = Object.keys(PLAN_FIELDS)
      .map((fieldName) => {
        const field = PLAN_FIELDS[fieldName as keyof typeof PLAN_FIELDS];
        if ("error" in field) {
          return field.error?.(editingPlan);
        }
      })
      .filter((x) => !!x);

    if (errors.length) {
      sendNotification({
        title: "Error saving plan",
        type: "error",
        description: `${errors[0]}`,
      });
      return;
    }

    // check if plan code is unique
    const plansWithSameCode = plans?.filter(
      (plan) => plan.code.trim() === editingPlan.code.trim()
    );
    if (
      (editingPlan.isNewPlan && plansWithSameCode.length > 0) ||
      (!editingPlan.isNewPlan && plansWithSameCode.length > 1)
    ) {
      sendNotification({
        title: "Error saving plan",
        type: "error",
        description: "Plan code must be unique",
      });
      return;
    }

    const requestBody: PlanType = toPlanType(editingPlan);

    const r = await clientFetch("/api/poke/plans", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(
        requestBody satisfies z.infer<typeof PlanTypeSchema>
      ),
    });
    if (!r.ok) {
      sendNotification({
        title: "Error saving plan",
        type: "error",
        description: `Something went wrong: ${r.status} ${await r.text()}`,
      });
      return;
    }

    await mutate("/api/poke/plans");

    resetEditingPlan();
  };

  const plansToRender: EditingPlanType[] = (plans || []).map(fromPlanType);
  if (editingPlan?.isNewPlan) {
    plansToRender.push(editingPlan);
  }

  return isPlansLoading ? (
    <Spinner />
  ) : (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="h-full py-8 text-2xl font-bold">Plans</div>
      <div className="h-full w-full overflow-x-auto pb-52 pt-12">
        <table className="mx-auto h-full table-auto overflow-visible rounded-lg">
          <thead className="bg-muted dark:bg-muted-night">
            <tr>
              {Object.keys(PLAN_FIELDS).map((fieldName) => {
                const field =
                  PLAN_FIELDS[fieldName as keyof typeof PLAN_FIELDS];
                return (
                  <th key={fieldName}>
                    {"IconComponent" in field ? (
                      <div className="flex flex-row justify-center">
                        <field.IconComponent />
                      </div>
                    ) : (
                      field.title
                    )}
                  </th>
                );
              })}
              <th className="px-4 py-2">Edit</th>
            </tr>
          </thead>
          <tbody className="h-full bg-background pb-48 text-primary-light dark:bg-background-night dark:text-primary-light-night">
            {plansToRender?.map((plan) => {
              const planId = plan.isNewPlan ? "newPlan" : plan.code;

              return (
                <tr key={planId}>
                  {Object.keys(PLAN_FIELDS).map((fieldName) => (
                    <React.Fragment key={`${planId}:${fieldName}`}>
                      <Field
                        plan={plan}
                        fieldName={fieldName as keyof typeof PLAN_FIELDS}
                        isEditing={
                          (!editingPlan?.isNewPlan &&
                            editingPlan?.code === plan.code) ||
                          (!!editingPlan?.isNewPlan && !!plan.isNewPlan)
                        }
                        setEditingPlan={setEditingPlan}
                        editingPlan={editingPlan}
                      />
                    </React.Fragment>
                  ))}
                  <td className="w-12 min-w-16 flex-none border px-4 py-2">
                    {plan.code === editingPlan?.code || plan.isNewPlan ? (
                      <div className="flex flex-row justify-center">
                        <IconButton icon={Check} onClick={handleSavePlan} />
                        <IconButton icon={XClose} onClick={resetEditingPlan} />
                      </div>
                    ) : (
                      <div className="flex flex-row justify-center">
                        <IconButton
                          icon={Edit04}
                          onClick={() => setEditingPlan(plan)}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div>
        <Button
          icon={Plus}
          label="Create a new plan"
          variant="outline"
          onClick={() => createNewPlan()}
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          disabled={editingPlan?.isNewPlan || !!editingPlan}
        />
      </div>
    </div>
  );
}
